import React, { useEffect, useRef } from 'react';
import './LegalDocumentModal.css';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Legal Document Modal Component
 * Displays legal documents (AGB, Datenschutz, Community Guidelines) in a fullscreen modal
 * Matches the Universe website design with accessibility support
 *
 * Note: Legal documents are only available in German as per German legal requirements
 */

// Legal document content - German only as per legal requirements
const LEGAL_CONTENT = {
  terms: {
    title: 'Allgemeine Geschäftsbedingungen (AGB)',
    titleEn: 'Terms and Conditions',
    content: `================================================================================
                    ALLGEMEINE GESCHÄFTSBEDINGUNGEN (AGB)
                              UNIVERSE App
================================================================================

                    Stand: 12. Juli 2026
                    Version: 1.2

================================================================================
                         INHALTSVERZEICHNIS
================================================================================

1.  Geltungsbereich und Vertragsgegenstand
2.  Registrierung und Vertragsschluss
3.  Leistungsbeschreibung
4.  Pflichten der Nutzer
5.  Nutzerinhalte und Rechteeinräumung
6.  Marktplatz
7.  Kursbewertungen
8.  Anonyme Beiträge
9.  Chat-Funktion
10. Melde- und Beschwerdeverfahren
11. Haftung
12. Laufzeit und Kündigung
13. Änderungen der AGB
14. Datenschutz
15. Schlussbestimmungen

================================================================================
                        1. GELTUNGSBEREICH UND VERTRAGSGEGENSTAND
================================================================================

1.1 Betreiber und Anwendungsbereich

Diese Allgemeinen Geschäftsbedingungen (nachfolgend "AGB") regeln die
Nutzung der UNIVERSE App (nachfolgend "App" oder "Plattform"), die als
Web-Anwendung und mobile Anwendung für Android und iOS verfügbar ist.

Betreiber der Plattform ist:
GAIA Software UG (haftungsbeschränkt)
Gerhardstraße 6
22926 Ahrensburg
Deutschland

E-Mail: kontakt@universe-app.online

Handelsregister: Amtsgericht Lübeck, HRB-27728
Geschäftsführer: Jihyun Lee, Julius von Sulecki

1.2 Zielgruppe

Die Plattform richtet sich ausschließlich an Studierende, die an einer
deutschen Hochschule eingeschrieben sind und über eine gültige
universitäre E-Mail-Adresse verfügen. Die Nutzung ist auf das Gebiet
der Bundesrepublik Deutschland beschränkt.

1.3 Geltung der AGB

Diese AGB gelten für alle Verträge zwischen dem Betreiber und den
Nutzern über die Nutzung der Plattform. Abweichende Bedingungen des
Nutzers finden keine Anwendung, es sei denn, der Betreiber stimmt ihrer
Geltung ausdrücklich schriftlich zu.

1.4 Änderungen der AGB

Der Betreiber behält sich vor, diese AGB mit Wirkung für die Zukunft
zu ändern, soweit dies unter Berücksichtigung der Interessen des
Betreibers für den Nutzer zumutbar ist. Über Änderungen wird der
Nutzer mindestens 30 Tage vor Inkrafttreten per E-Mail informiert.
Widerspricht der Nutzer nicht innerhalb von 30 Tagen nach Zugang der
Änderungsmitteilung, gelten die geänderten AGB als angenommen. Der
Nutzer wird in der Änderungsmitteilung auf diese Rechtsfolge
hingewiesen. Bei Widerspruch steht beiden Parteien ein Kündigungsrecht zu.

================================================================================
                        2. REGISTRIERUNG UND VERTRAGSSCHLUSS
================================================================================

2.1 Registrierungsvoraussetzungen

Zur Nutzung der Plattform ist eine Registrierung erforderlich. Zur
Registrierung berechtigt sind ausschließlich:

a) Natürliche Personen, die das 16. Lebensjahr vollendet haben
b) Personen, die an einer deutschen Hochschule eingeschrieben sind
c) Personen, die über eine gültige universitäre E-Mail-Adresse
   (Domain einer deutschen Hochschule) verfügen

2.2 Registrierungsprozess

Die Registrierung erfolgt durch:

a) Eingabe des Vornamens und Nachnamens
b) Eingabe einer gültigen universitären E-Mail-Adresse
c) Festlegung eines Passworts gemäß den Sicherheitsanforderungen
d) Bestätigung der Kenntnisnahme dieser AGB durch aktives Anklicken
   eines nicht vorausgefüllten Kontrollkästchens
e) Bestätigung der Kenntnisnahme der Datenschutzerklärung durch
   separates aktives Anklicken eines nicht vorausgefüllten
   Kontrollkästchens
f) Absenden des Registrierungsformulars

2.3 E-Mail-Verifizierung

Nach Absenden des Registrierungsformulars erhält der Nutzer eine
Bestätigungs-E-Mail an die angegebene universitäre E-Mail-Adresse.
Der Nutzungsvertrag kommt erst mit erfolgreicher Verifizierung der
E-Mail-Adresse durch Klick auf den Bestätigungslink zustande.

2.4 Konto und Zugangsdaten

Der Nutzer ist verpflichtet:

a) Bei der Registrierung wahrheitsgetreue und vollständige Angaben
   zu machen
b) Seine Zugangsdaten geheim zu halten und vor dem Zugriff Dritter
   zu schützen
c) Den Betreiber unverzüglich zu informieren, wenn er Kenntnis
   davon erlangt, dass Dritte seine Zugangsdaten missbrauchen
d) Sein Konto ausschließlich persönlich zu nutzen

2.5 Mehrfachkonten

Jeder Nutzer darf nur ein Konto anlegen. Die Erstellung mehrerer
Konten ist untersagt und berechtigt den Betreiber zur Sperrung
sämtlicher Konten des Nutzers.

================================================================================
                        3. LEISTUNGSBESCHREIBUNG
================================================================================

3.1 Grundlegende Dienste

Die Plattform stellt Studierenden deutscher Hochschulen folgende
kostenlose Dienste zur Verfügung:

a) Beiträge (Posts): Erstellung, Bearbeitung und Löschung von
   Textbeiträgen mit optionalen Bildern, sichtbar für Studierende
   derselben Hochschule
b) Marktplatz: Erstellung von Angeboten zum Verkauf oder zur
   kostenlosen Abgabe von Gegenständen
c) Chat: Private Kommunikation zwischen Nutzern, insbesondere im
   Rahmen von Marktplatztransaktionen
d) Kursbewertungen: Bewertung von Lehrveranstaltungen der eigenen
   Hochschule
e) Benachrichtigungen: Information über relevante Aktivitäten
   (Kommentare, Likes, Nachrichten)

3.2 Verfügbarkeit

Der Betreiber bemüht sich um eine hohe Verfügbarkeit der Plattform,
garantiert jedoch keine ununterbrochene Erreichbarkeit. Wartungsarbeiten
werden nach Möglichkeit angekündigt. Der Betreiber haftet nicht für
Nichtverfügbarkeit aufgrund von Umständen außerhalb seines
Einflussbereichs (z.B. höhere Gewalt, Störungen im Internet).

3.3 Weiterentwicklung

Der Betreiber ist berechtigt, die angebotenen Dienste jederzeit zu
erweitern, zu ändern oder einzustellen, soweit dies für den Nutzer
zumutbar ist. Wesentliche Änderungen werden mit angemessener Frist
angekündigt.

================================================================================
                        4. PFLICHTEN DER NUTZER
================================================================================

4.1 Allgemeine Verhaltenspflichten

Der Nutzer verpflichtet sich:

a) Die Plattform nur zu rechtmäßigen Zwecken zu nutzen
b) Alle anwendbaren Gesetze und Vorschriften einzuhalten
c) Die Rechte anderer Nutzer und Dritter zu respektieren
d) Keine falschen oder irreführenden Angaben zu machen

4.2 Verbotene Inhalte

Es ist untersagt, Inhalte zu veröffentlichen, die:

a) Gegen geltendes Recht verstoßen, insbesondere strafbare Inhalte
b) Die Rechte Dritter verletzen (Urheberrechte, Markenrechte,
   Persönlichkeitsrechte)
c) Beleidigend, bedrohend, diskriminierend oder menschenverachtend sind
d) Gewalt verherrlichen oder zu Gewalt aufrufen
e) Pornografische oder jugendgefährdende Inhalte darstellen
f) Falsche Tatsachenbehauptungen enthalten
g) Spam, Werbung oder kommerzielle Inhalte ohne Genehmigung enthalten
h) Schadsoftware oder Links zu schädlichen Inhalten enthalten
i) Die Privatsphäre anderer Nutzer verletzen (z.B. Veröffentlichung
   persönlicher Daten ohne Einwilligung)
j) Zur Umgehung der Plattformregeln geeignet sind

4.3 Verbotene Verhaltensweisen

Es ist untersagt:

a) Die Plattform zu kommerziellen Zwecken zu nutzen (außer über den
   Marktplatz für private Verkäufe)
b) Die Identität anderer Personen anzunehmen oder sich als andere
   Person auszugeben
c) Automatisierte Zugriffe auf die Plattform vorzunehmen (Bots, Scraper)
d) Sicherheitsmechanismen der Plattform zu umgehen oder zu stören
e) Die Infrastruktur der Plattform übermäßig zu belasten
f) Andere Nutzer zu belästigen oder zu stalken

4.4 Meldepflicht

Der Nutzer ist verpflichtet, erkannte Verstöße gegen diese AGB oder
rechtswidrige Inhalte unverzüglich über die Meldefunktion der
Plattform anzuzeigen.

================================================================================
                        5. NUTZERINHALTE UND RECHTEEINRÄUMUNG
================================================================================

5.1 Eigentum an Nutzerinhalten

Der Nutzer behält sämtliche Urheberrechte und sonstigen Rechte an den
von ihm erstellten Inhalten (nachfolgend "Nutzerinhalte"). Nutzerinhalte
umfassen insbesondere Beiträge, Kommentare, Bilder, Marktplatzanzeigen,
Kursbewertungen und Chat-Nachrichten.

5.2 Lizenzeinräumung an den Betreiber

Mit dem Hochladen oder Erstellen von Nutzerinhalten räumt der Nutzer
dem Betreiber das nicht-ausschließliche, übertragbare,
unterlizenzierbare, zeitlich und räumlich unbeschränkte Recht ein,
die Nutzerinhalte im Rahmen des Betriebs der Plattform zu nutzen,
insbesondere:

a) Zu speichern und zu vervielfältigen
b) Anderen Nutzern anzuzeigen und zugänglich zu machen
c) Technisch zu bearbeiten (z.B. Formatanpassung, Komprimierung)
d) Im Rahmen von Sicherungskopien zu vervielfältigen

Diese Lizenz dient ausschließlich dem technischen Betrieb der Plattform
und endet mit Löschung des Nutzerinhalts durch den Nutzer, soweit
keine gesetzlichen Aufbewahrungspflichten bestehen.

5.3 Gewährleistungen des Nutzers

Der Nutzer gewährleistet, dass:

a) Er berechtigt ist, die Nutzerinhalte zu erstellen und die Rechte
   gemäß Ziffer 5.2 einzuräumen
b) Die Nutzerinhalte keine Rechte Dritter verletzen
c) Die Nutzerinhalte nicht gegen geltendes Recht verstoßen

5.4 Freistellung

Der Nutzer stellt den Betreiber von sämtlichen Ansprüchen Dritter
frei, die diese aufgrund einer Verletzung ihrer Rechte durch
Nutzerinhalte geltend machen. Der Nutzer übernimmt die Kosten einer
angemessenen Rechtsverteidigung des Betreibers. Dies gilt nicht,
soweit der Nutzer die Rechtsverletzung nicht zu vertreten hat.

5.5 Recht zur Entfernung

Der Betreiber ist berechtigt, Nutzerinhalte zu entfernen oder deren
Sichtbarkeit einzuschränken, wenn:

a) Ein Verstoß gegen diese AGB vorliegt oder vermutet wird
b) Eine Verletzung von Rechten Dritter vorliegt oder vermutet wird
c) Eine behördliche oder gerichtliche Anordnung dies erfordert
d) Dies aus technischen Gründen erforderlich ist

Der Nutzer wird über Maßnahmen gemäß Ziffer 10 informiert.

================================================================================
                        6. MARKTPLATZ
================================================================================

6.1 Funktion des Marktplatzes

Der Marktplatz ermöglicht Nutzern, Gegenstände anderen Nutzern zum
Kauf oder zur kostenlosen Abgabe anzubieten. Der Betreiber stellt
lediglich die technische Infrastruktur bereit und ist nicht selbst
Vertragspartei der zwischen Nutzern geschlossenen Verträge.

6.2 Angebote erstellen

Der Nutzer kann Angebote mit folgenden Informationen erstellen:

a) Titel und Beschreibung des Gegenstands
b) Bilder des Gegenstands
c) Preis oder Kennzeichnung als kostenlose Abgabe
d) Standort (optional)

6.3 Verbotene Angebote

Es ist untersagt, folgende Gegenstände anzubieten:

a) Gegenstände, deren Verkauf gesetzlich verboten ist
b) Gestohlene oder illegal erworbene Gegenstände
c) Waffen, Munition oder explosive Stoffe
d) Drogen, Betäubungsmittel oder deren Zubehör
e) Rezeptpflichtige Medikamente
f) Jugendgefährdende Medien
g) Fälschungen oder Produktpiraterie
h) Lebende Tiere
i) Dienstleistungen oder gewerbliche Angebote
j) Gegenstände, die Rechte Dritter verletzen

6.4 Vertragsschluss zwischen Nutzern

Der Kauf- oder Schenkungsvertrag kommt ausschließlich zwischen den
beteiligten Nutzern zustande. Der Betreiber ist nicht Vertragspartei
und übernimmt keine Gewährleistung für die angebotenen Gegenstände
oder deren Beschreibung.

6.5 Pflichten des Anbieters

Der Anbieter verpflichtet sich:

a) Die Gegenstände wahrheitsgemäß und vollständig zu beschreiben
b) Nur Gegenstände anzubieten, zu deren Verkauf er berechtigt ist
c) Mängel oder Beschädigungen anzugeben
d) Verkaufte Gegenstände als verkauft zu markieren oder das Angebot
   zu entfernen

6.6 Keine Gebühren

Die Nutzung des Marktplatzes ist derzeit kostenlos. Der Betreiber
behält sich vor, künftig Gebühren einzuführen, worüber Nutzer
rechtzeitig informiert werden.

================================================================================
                        7. KURSBEWERTUNGEN
================================================================================

7.1 Zweck der Kursbewertungen

Die Funktion ermöglicht Studierenden, Lehrveranstaltungen ihrer
Hochschule zu bewerten, um anderen Studierenden bei der Kursauswahl
zu helfen.

7.2 Bewertungsberechtigung

Zur Abgabe einer Bewertung ist berechtigt, wer:

a) An der entsprechenden Hochschule eingeschrieben ist
b) Den zu bewertenden Kurs besucht hat oder besucht

7.3 Inhalt der Bewertungen

Bewertungen müssen:

a) Auf eigenem Erleben basieren
b) Sachlich und fair formuliert sein
c) Tatsachen von Meinungen unterscheidbar machen
d) Die Persönlichkeitsrechte der Dozierenden wahren

7.4 Verbotene Bewertungsinhalte

Es ist untersagt:

a) Falsche Tatsachenbehauptungen aufzustellen
b) Beleidigende oder verleumderische Aussagen zu machen
c) Bewertungen für nicht besuchte Kurse abzugeben
d) Bewertungen im Auftrag Dritter abzugeben
e) Bewertungen zu manipulieren (z.B. Mehrfachbewertungen)

7.5 Haftung für Bewertungen

Der Nutzer haftet für den Inhalt seiner Bewertungen. Der Betreiber
überprüft Bewertungen nicht proaktiv, entfernt jedoch Bewertungen
nach Kenntniserlangung, die gegen diese AGB verstoßen.

================================================================================
                        8. ANONYME BEITRÄGE
================================================================================

8.1 Möglichkeit anonymer Beiträge

Die Plattform ermöglicht Nutzern, Beiträge und Kommentare anonym zu
veröffentlichen. Bei anonymen Beiträgen wird anderen Nutzern lediglich
"Anonym" anstelle des Nutzernamens angezeigt.

8.2 Umfang der Anonymität

Die Anonymität schützt die Identität des Nutzers gegenüber anderen
Nutzern der Plattform. Der Betreiber speichert jedoch auch bei anonymen
Beiträgen technische Daten (z.B. IP-Adresse, Konto-ID), die eine
Identifizierung ermöglichen.

8.3 Grenzen der Anonymität

Die Anonymität schützt nicht vor:

a) Auskunftsersuchen von Strafverfolgungsbehörden
b) Gerichtlichen Anordnungen zur Offenlegung
c) Der Durchsetzung von Rechten Dritter bei rechtswidrigen Inhalten

Der Betreiber ist berechtigt und gegebenenfalls verpflichtet, die
Identität des Nutzers auf behördliche oder gerichtliche Anordnung
offenzulegen.

8.4 Geltung der AGB

Die anonyme Nutzung entbindet nicht von der Einhaltung dieser AGB.
Insbesondere gelten die Verbote gemäß Ziffer 4 unverändert.

================================================================================
                        9. CHAT-FUNKTION
================================================================================

9.1 Zweck der Chat-Funktion

Die Chat-Funktion ermöglicht die private Kommunikation zwischen
Nutzern, insbesondere zur Abwicklung von Marktplatztransaktionen.

9.2 Vertraulichkeit

Chat-Nachrichten sind nur für die beteiligten Nutzer sichtbar. Der
Betreiber greift auf Chat-Inhalte nur zu, wenn:

a) Dies zur technischen Bereitstellung des Dienstes erforderlich ist
b) Ein begründeter Verdacht auf Rechtsverstoß besteht
c) Eine behördliche oder gerichtliche Anordnung vorliegt

9.3 Speicherung

Chat-Nachrichten werden für die Dauer des Bestehens der Konten der
beteiligten Nutzer gespeichert. Näheres regelt die Datenschutzerklärung.

9.4 Verbotene Inhalte

Die Regelungen zu verbotenen Inhalten gemäß Ziffer 4.2 gelten für
Chat-Nachrichten entsprechend. Insbesondere ist es untersagt, andere
Nutzer über den Chat zu belästigen, zu bedrohen oder zu beleidigen.

9.5 Blockieren von Nutzern

Der Nutzer kann andere Nutzer blockieren, um keine weiteren
Nachrichten von diesen zu erhalten. Bei Blockierung:

a) Wird die Konversation aus der Nachrichtenübersicht des
   blockierenden Nutzers entfernt
b) Kann der blockierte Nutzer weiterhin Nachrichten senden, diese
   werden jedoch nicht an den blockierenden Nutzer zugestellt
c) Wird der blockierte Nutzer nicht über die Blockierung informiert
d) Kann der blockierende Nutzer die Blockierung jederzeit aufheben

Das Blockieren entbindet nicht von der Einhaltung dieser AGB.
Missbräuchliches Blockieren (z.B. zur Vermeidung berechtigter
Reklamationen bei Marktplatztransaktionen) kann als Verstoß
gewertet werden.

9.6 Benachrichtigungseinstellungen für einzelne Chats

Der Nutzer kann Benachrichtigungen für einzelne Chat-Konversationen
deaktivieren. In diesem Fall:

a) Werden keine Push-Benachrichtigungen für neue Nachrichten gesendet
b) Werden keine Benachrichtigungen in der Benachrichtigungsübersicht
   angezeigt
c) Bleibt die Anzahl ungelesener Nachrichten im Chat sichtbar
d) Kann die Einstellung jederzeit wieder aktiviert werden

================================================================================
                        10. MELDE- UND BESCHWERDEVERFAHREN
================================================================================

10.1 Meldung von Inhalten

Jeder Nutzer kann Inhalte, die gegen diese AGB oder geltendes Recht
verstoßen, über die Meldefunktion der Plattform melden. Die Meldung
sollte enthalten:

a) Art des Verstoßes
b) Begründung der Meldung
c) Falls zutreffend: Nachweise für den Verstoß

10.2 Bearbeitung von Meldungen

Der Betreiber bearbeitet Meldungen zeitnah und objektiv. Er:

a) Prüft den gemeldeten Inhalt
b) Ergreift gegebenenfalls Maßnahmen (Entfernung, Einschränkung,
   Verwarnung, Kontosperre)
c) Informiert den meldenden Nutzer über das Ergebnis
d) Informiert den betroffenen Nutzer über ergriffene Maßnahmen
   und deren Begründung

10.2a Automatische Inhaltsmoderation

Die Plattform verwendet technische Systeme zur automatischen
Erkennung von unangemessenen Inhalten:

a) Wortfilter: Inhalte mit unangemessenen Wörtern oder Ausdrücken
   können nicht hochgeladen werden. Der Nutzer wird informiert,
   welche Wörter den Upload verhindern
b) Automatische Löschung: Inhalte, die von 5 oder mehr verschiedenen
   Nutzern gemeldet werden, werden automatisch entfernt
c) Benachrichtigung: Bei automatischer Löschung wird der Nutzer per
   E-Mail über die Entfernung und den Grund informiert
d) Wiederherstellung: Automatisch gelöschte Inhalte können auf
   Antrag vom Betreiber überprüft und wiederhergestellt werden

Die automatischen Systeme unterstützen die Moderation, ersetzen
jedoch keine menschliche Prüfung bei Beschwerden.

10.3 Beschwerde gegen Maßnahmen

Nutzer, gegen die Maßnahmen ergriffen wurden, können innerhalb von
14 Tagen Beschwerde einlegen. Die Beschwerde ist zu begründen. Der
Betreiber prüft die Beschwerde und teilt das Ergebnis mit.

10.4 Dokumentation

Der Betreiber dokumentiert eingegangene Meldungen, ergriffene
Maßnahmen und Beschwerden zu Transparenzzwecken.

================================================================================
                        11. HAFTUNG
================================================================================

11.1 Haftung des Betreibers

Die Haftung des Betreibers bestimmt sich nach folgenden Grundsätzen:

a) Für Vorsatz und grobe Fahrlässigkeit haftet der Betreiber
   unbeschränkt.

b) Für leichte Fahrlässigkeit haftet der Betreiber nur bei Verletzung
   wesentlicher Vertragspflichten (Kardinalpflichten). Kardinalpflichten
   sind solche Pflichten, deren Erfüllung die ordnungsgemäße
   Durchführung des Vertrages überhaupt erst ermöglicht und auf deren
   Einhaltung der Nutzer regelmäßig vertrauen darf. In diesen Fällen
   ist die Haftung auf den typischen, vorhersehbaren Schaden beschränkt.

c) Die Haftung für Schäden aus der Verletzung des Lebens, des Körpers
   oder der Gesundheit bleibt unbeschränkt.

d) Die Haftung nach dem Produkthaftungsgesetz bleibt unberührt.

11.2 Haftung für Nutzerinhalte

Der Betreiber haftet nicht für Nutzerinhalte. Eine Überwachungspflicht
besteht nicht. Nach Kenntniserlangung von rechtswidrigen Inhalten
entfernt der Betreiber diese unverzüglich.

11.3 Haftung für Marktplatztransaktionen

Der Betreiber haftet nicht für:

a) Die Richtigkeit von Angebotsbeschreibungen
b) Die Qualität oder Mängelfreiheit angebotener Gegenstände
c) Die Erfüllung von Verträgen zwischen Nutzern
d) Schäden aus Transaktionen zwischen Nutzern

11.4 Haftung für Drittinhalte

Der Betreiber haftet nicht für Inhalte verlinkter externer Websites.
Für diese Inhalte ist ausschließlich der jeweilige Anbieter
verantwortlich.

11.5 Erfüllungsgehilfen

Die vorstehenden Haftungsbeschränkungen gelten auch zugunsten der
gesetzlichen Vertreter, Angestellten und Erfüllungsgehilfen des
Betreibers.

================================================================================
                        12. LAUFZEIT UND KÜNDIGUNG
================================================================================

12.1 Vertragsbeginn und -laufzeit

Der Nutzungsvertrag beginnt mit erfolgreicher E-Mail-Verifizierung
gemäß Ziffer 2.3 und wird auf unbestimmte Zeit geschlossen.

12.2 Kündigung durch den Nutzer

Der Nutzer kann den Vertrag jederzeit ohne Angabe von Gründen
kündigen. Die Kündigung erfolgt durch Löschung des Kontos in den
Profileinstellungen oder durch Mitteilung an den Betreiber.

12.2a Kontolöschung mit Wiederherstellungsfrist

Bei Beantragung der Kontolöschung durch den Nutzer gilt eine
30-tägige Wiederherstellungsfrist:

a) Der Nutzer kann innerhalb von 30 Tagen nach Löschantrag sein
   Konto durch erneuten Login wiederherstellen
b) Während der Wiederherstellungsfrist werden die Kontodaten
   gespeichert, aber das Konto ist nicht aktiv
c) Nach Ablauf der 30 Tage werden personenbezogene Daten und
   Marktplatzangebote unwiderruflich gelöscht
d) Beiträge, Kommentare und Kursbewertungen werden anonymisiert
   und bleiben erhalten
e) Der Nutzer wird bei Löschantrag über diese Regelung informiert

12.3 Kündigung durch den Betreiber

Der Betreiber kann den Vertrag mit einer Frist von 30 Tagen kündigen.
Die Kündigung erfolgt per E-Mail unter Angabe der Gründe.

12.4 Außerordentliche Kündigung

Beide Parteien sind zur außerordentlichen Kündigung aus wichtigem
Grund berechtigt. Ein wichtiger Grund liegt für den Betreiber
insbesondere vor bei:

a) Schwerwiegenden oder wiederholten Verstößen gegen diese AGB
b) Vorliegen von Straftaten im Zusammenhang mit der Plattformnutzung
c) Bereitstellung falscher Identitätsinformationen
d) Missbrauch der Plattform entgegen ihrem Zweck

Bei außerordentlicher Kündigung erfolgt eine sofortige Sperrung des
Kontos. Der Nutzer wird unter Angabe der Gründe informiert.

12.5 Löschung wegen Inaktivität

Der Betreiber ist berechtigt, Konten nach 12 Monaten durchgehender
Inaktivität (kein Login) zu löschen. Der Nutzer wird:

a) 30 Tage vor der geplanten Löschung per E-Mail gewarnt
b) Über die Möglichkeit informiert, durch Login die Löschung
   abzuwenden

12.6 Folgen der Beendigung

Bei Beendigung des Vertrages:

a) Wird das Konto deaktiviert und nach Ablauf gesetzlicher
   Aufbewahrungsfristen gelöscht
b) Werden Marktplatzanzeigen entfernt
c) Bleiben Beiträge und Bewertungen in anonymisierter Form erhalten,
   soweit der Nutzer nicht deren Löschung verlangt
d) Erlöschen die Lizenzrechte gemäß Ziffer 5.2, soweit nicht für
   bereits veröffentlichte Inhalte erforderlich

================================================================================
                        13. ÄNDERUNGEN DER AGB
================================================================================

13.1 Änderungsrecht

Der Betreiber ist berechtigt, diese AGB zu ändern, wenn:

a) Dies aufgrund geänderter Rechtsvorschriften erforderlich ist
b) Dies aufgrund von Rechtsprechung erforderlich ist
c) Neue Funktionen eingeführt werden
d) Dies zur Schließung von Regelungslücken erforderlich ist

Änderungen erfolgen nur, soweit sie für den Nutzer zumutbar sind.

13.2 Benachrichtigung

Über Änderungen informiert der Betreiber den Nutzer mindestens
30 Tage vor deren Inkrafttreten per E-Mail. Die Mitteilung enthält:

a) Den vollständigen Text der geänderten AGB
b) Eine Gegenüberstellung der wesentlichen Änderungen
c) Das Datum des Inkrafttretens
d) Den Hinweis auf das Widerspruchsrecht und dessen Folgen

13.3 Zustimmung zu Änderungen

Widerspricht der Nutzer den geänderten AGB nicht innerhalb von 30
Tagen nach Zugang der Mitteilung, gelten die geänderten AGB als
angenommen. Bei Widerspruch kann der Betreiber das Vertragsverhältnis
mit Wirkung zum Zeitpunkt des Inkrafttretens der Änderung kündigen.

================================================================================
                        14. DATENSCHUTZ
================================================================================

14.1 Datenschutzerklärung

Die Erhebung und Verarbeitung personenbezogener Daten erfolgt gemäß
der separaten Datenschutzerklärung, die unter [URL der
Datenschutzerklärung] abrufbar ist.

14.2 Datenverarbeitung

Die Datenschutzerklärung informiert insbesondere über:

a) Art und Umfang der erhobenen Daten
b) Zwecke der Datenverarbeitung
c) Rechtsgrundlagen der Verarbeitung
d) Empfänger der Daten
e) Speicherdauer
f) Rechte der betroffenen Personen

14.3 Einwilligung

Soweit die Datenverarbeitung auf der Einwilligung des Nutzers beruht,
kann diese jederzeit mit Wirkung für die Zukunft widerrufen werden.

================================================================================
                        15. SCHLUSSBESTIMMUNGEN
================================================================================

15.1 Anwendbares Recht

Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des
UN-Kaufrechts. Zwingende Verbraucherschutzvorschriften des Staates,
in dem der Nutzer seinen gewöhnlichen Aufenthalt hat, bleiben unberührt.

15.2 Gerichtsstand

Ist der Nutzer Kaufmann, juristische Person des öffentlichen Rechts
oder öffentlich-rechtliches Sondervermögen, ist ausschließlicher
Gerichtsstand für alle Streitigkeiten aus diesem Vertrag der Sitz
des Betreibers. Für Verbraucher gelten die gesetzlichen
Zuständigkeitsregelungen.

15.3 Online-Streitbeilegung

Die Europäische Kommission stellt eine Plattform zur
Online-Streitbeilegung (OS) bereit, die unter
https://ec.europa.eu/consumers/odr/ erreichbar ist. Der Betreiber ist
weder bereit noch verpflichtet, an einem Streitbeilegungsverfahren vor
einer Verbraucherschlichtungsstelle teilzunehmen.

15.4 Salvatorische Klausel

Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden,
bleibt die Wirksamkeit der übrigen Bestimmungen unberührt. Die
unwirksame Bestimmung ist durch eine wirksame zu ersetzen, die dem
wirtschaftlichen Zweck der unwirksamen Bestimmung am nächsten kommt.

15.5 Keine Abtretung

Der Nutzer ist nicht berechtigt, Rechte aus diesem Vertrag ohne
vorherige schriftliche Zustimmung des Betreibers an Dritte abzutreten.

15.6 Vollständigkeit

Diese AGB enthalten alle Vereinbarungen zwischen den Parteien
hinsichtlich des Vertragsgegenstands. Mündliche Nebenabreden bestehen
nicht.

================================================================================
                        KONTAKT
================================================================================

Bei Fragen zu diesen AGB wenden Sie sich bitte an:

GAIA Software UG (haftungsbeschränkt)
Gerhardstraße 6
22926 Ahrensburg

E-Mail: kontakt@universe-app.online

================================================================================
                        VERSIONSHISTORIE
================================================================================

Version 1.2 - 12. Juli 2026
- Korrektur der Kontakt-E-Mail-Adresse auf kontakt@universe-app.online

Version 1.1 - 28. Januar 2026
- Ergänzung Ziffer 9.5: Blockieren von Nutzern
- Ergänzung Ziffer 9.6: Benachrichtigungseinstellungen für einzelne Chats
- Ergänzung Ziffer 10.2a: Automatische Inhaltsmoderation (Wortfilter,
  automatische Löschung bei Mehrfachmeldungen)
- Ergänzung Ziffer 12.2a: Kontolöschung mit 30-tägiger Wiederherstellungsfrist

Version 1.0 - 05. Januar 2026
- Erstveröffentlichung der Allgemeinen Geschäftsbedingungen

================================================================================
                    Ende der Allgemeinen Geschäftsbedingungen
================================================================================`
  },
  privacy: {
    title: 'Datenschutzerklärung',
    titleEn: 'Privacy Policy',
    content: `DATENSCHUTZERKLÄRUNG

Stand: 1. Juli 2026

================================================================================
1. VERANTWORTLICHER
================================================================================

Verantwortlich für die Datenverarbeitung auf dieser Plattform ist:

GAIA Software UG (haftungsbeschränkt)
Gerhardstraße 6
22926 Ahrensburg
Deutschland

E-Mail: datenschutz@universe-app.online

================================================================================
2. DATENSCHUTZBEAUFTRAGTER
================================================================================

Ein Datenschutzbeauftragter wurde nicht bestellt, da hierzu derzeit keine gesetzliche Verpflichtung besteht. Bei Fragen zum Datenschutz können Sie sich jederzeit an die oben genannte Kontaktadresse wenden.

================================================================================
3. ALLGEMEINE HINWEISE
================================================================================

3.1 Geltungsbereich

Diese Datenschutzerklärung gilt für die Nutzung der Plattform UNIVERSE
(nachfolgend "Plattform" oder "Dienst"), einem sozialen Netzwerk
ausschließlich für Studierende an deutschen Hochschulen.

3.2 Rechtsgrundlagen

Wir verarbeiten Ihre personenbezogenen Daten auf Grundlage der folgenden
Rechtsvorschriften:

- EU-Datenschutz-Grundverordnung (DSGVO)
- Bundesdatenschutzgesetz (BDSG)
- Telekommunikation-Digitale-Dienste-Datenschutz-Gesetz (TDDDG)

3.3 Grundsätze der Datenverarbeitung

Wir verarbeiten Ihre Daten nur:
- Wenn eine Rechtsgrundlage dies erlaubt (Art. 6 DSGVO)
- Für festgelegte, eindeutige und legitime Zwecke
- In dem Umfang, der für diese Zwecke erforderlich ist
- Unter Gewährleistung angemessener Sicherheit

================================================================================
4. ERHEBUNG UND VERARBEITUNG PERSONENBEZOGENER DATEN
================================================================================

4.1 Bei der Registrierung

Bei der Erstellung eines Benutzerkontos erheben wir folgende Daten:

| Datenfeld           | Zweck                                      | Rechtsgrundlage      |
|---------------------|--------------------------------------------|-----------------------|
| Vorname             | Identifikation, Personalisierung           | Art. 6(1)(b) DSGVO   |
| Nachname            | Identifikation                             | Art. 6(1)(b) DSGVO   |
| E-Mail-Adresse      | Authentifizierung, Verifizierung, Kontakt  | Art. 6(1)(b) DSGVO   |
| Passwort (gehasht)  | Kontosicherheit                            | Art. 6(1)(b) DSGVO   |
| Hochschule          | Community-Zuordnung, Inhaltsfilterung      | Art. 6(1)(b) DSGVO   |

Die Angabe dieser Daten ist für die Nutzung der Plattform erforderlich.
Ohne diese Daten kann kein Benutzerkonto erstellt werden.

Zusätzlich speichern wir den Zeitpunkt, zu dem Sie bei der Registrierung
unseren Nutzungsbedingungen (AGB) zugestimmt haben. Dies dient dem Nachweis
des Vertragsschlusses und der akzeptierten Vertragsbedingungen.
Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Dokumentation und Nachweisbarkeit des Vertragsschlusses).

Wir akzeptieren ausschließlich E-Mail-Adressen von deutschen Hochschulen,
um sicherzustellen, dass nur Studierende Zugang zur Plattform erhalten.

Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.

4.2 Profilbild

Sie können optional ein Profilbild hochladen. Dieses wird auf unseren
Servern gespeichert und anderen Nutzern angezeigt.

- Maximale Dateigröße: 5 MB
- Erlaubte Formate: JPEG, PNG, WebP
- Speicherort: EU-Server
- Löschung: Bei Kontolöschung oder manueller Entfernung

Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.

4.3 Nutzergenerierte Inhalte

Bei der Nutzung unserer Plattform können Sie folgende Inhalte erstellen:

a) Beiträge (Posts)
   - Textinhalte
   - Bilder (max. 10 pro Beitrag, je max. 5 MB)
   - Zeitstempel der Erstellung
   - Zugehörigkeit zur Hochschule

b) Kommentare
   - Textinhalte
   - Zeitstempel
   - Bezug zum kommentierten Beitrag

c) Marktplatz-Anzeigen
   - Titel und Beschreibung
   - Preis
   - Bilder
   - Angebotstyp (Suche/Biete)

d) Kursbewertungen
   - Sternebewertung (1-5)
   - Bewertungstext
   - Kurs- und Hochschulzuordnung

e) Chat-Nachrichten
   - Nachrichteninhalt
   - Zeitstempel
   - Empfänger

Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.

4.4 Anonyme Beiträge

Sie haben die Möglichkeit, Beiträge und Kommentare anonym zu
veröffentlichen. In diesem Fall wird Ihr Name nicht öffentlich angezeigt.

Hinweis: Auch bei anonymen Beiträgen speichern wir intern eine Verknüpfung
zu Ihrem Benutzerkonto. Dies ist notwendig für:
- Inhaltsmoderation bei Verstößen gegen unsere Nutzungsbedingungen
- Erfüllung rechtlicher Anforderungen

4.5 Interaktionsdaten

Wir speichern Daten zu Ihren Interaktionen auf der Plattform:

| Aktion              | Gespeicherte Daten                         | Rechtsgrundlage      |
|---------------------|--------------------------------------------|-----------------------|
| Likes               | Beitrag-ID, Nutzer-ID, Zeitstempel         | Art. 6(1)(b) DSGVO   |
| Favoriten           | Marktplatz-Objekt-ID, Nutzer-ID            | Art. 6(1)(b) DSGVO   |
| Meldungen           | Gemeldeter Inhalt, Kategorie, Begründung   | Art. 6(1)(f) DSGVO   |
| Blockierungen       | Blockierender/Blockierter Nutzer-ID, Zeit  | Art. 6(1)(b) DSGVO   |

4.6 Nutzungsstatistiken

Zur Verbesserung der Plattform und zur Ermittlung von Nutzungskennzahlen
(z.B. Anzahl aktiver Nutzer pro Tag und Monat) speichern wir bestimmte
Aktivitäten, etwa das Aufrufen oder Erstellen von Beiträgen, Suchvorgänge
oder das Senden von Chat-Nachrichten. Erfasst werden dabei die Art der
Aktivität, der Zeitpunkt und Ihre Nutzer-ID.

Da diese Daten mit Ihrem Benutzerkonto verknüpft sind, handelt es sich um
personenbezogene (pseudonymisierte) Daten. Wir verwenden sie ausschließlich
intern und geben sie nicht an Dritte weiter. Für Statistiken werden sie zu
aggregierten, anonymen Kennzahlen zusammengefasst. Bei Löschung Ihres Kontos
werden die mit Ihnen verknüpften Aktivitätsdaten gelöscht; nur anonyme,
aggregierte Kennzahlen bleiben erhalten.

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Verbesserung und Weiterentwicklung der Plattform).

4.7 Feedback und Bewertungen

Sie können uns freiwillig Feedback zur Plattform geben, insbesondere eine
Weiterempfehlungsbewertung (Skala 1-10) mit optionalem Freitext. Beta-
Testerinnen und -Tester können zusätzlich eine Gesamtbewertung abgeben.
Diese Angaben speichern wir zusammen mit Ihrer Nutzer-ID.

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Qualitätsverbesserung).

4.8 Automatisch erfasste Daten

Bei der Nutzung der Plattform werden automatisch folgende Daten erfasst:

a) Session-Daten
   - Session-Token (zufällig generiert)
   - Erstellungszeitpunkt
   - Ablaufzeitpunkt (24 Stunden)
   - Letzte Aktivität

b) Protokolldaten (Server-Logs)
   - IP-Adresse
   - Zeitpunkt des Zugriffs
   - Aufgerufene Seiten
   - Browsertyp und -version
   - Betriebssystem

c) Fehlerprotokolle
   - Bei technischen Fehlern speichern wir Angaben zum Fehler, ggf. die
     betroffene Nutzer-ID, die IP-Adresse und den aufgerufenen Endpunkt.
   - Zweck: Fehleranalyse und Sicherstellung des technischen Betriebs.

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Sicherheit, Missbrauchsprävention, technischer Betrieb).

================================================================================
5. COOKIES UND SESSION-MANAGEMENT
================================================================================

5.1 Technisch notwendige Cookies

Wir verwenden ausschließlich technisch notwendige Session-Cookies für
die Authentifizierung:

| Cookie-Name    | Zweck                          | Speicherdauer | Einwilligung erforderlich |
|----------------|--------------------------------|---------------|---------------------------|
| session_token  | Benutzerauthentifizierung      | 24 Stunden    | Nein (technisch notwendig)|

Diese Cookies sind für den Betrieb der Plattform zwingend erforderlich
und fallen unter die Ausnahme nach § 25 TDDDG für "unbedingt erforderliche"
Cookies. Eine Einwilligung ist daher nicht erforderlich.

5.2 Keine Tracking-Cookies

Wir verwenden KEINE:
- Analyse-Cookies (z.B. Google Analytics)
- Werbe-Cookies
- Social-Media-Tracking
- Drittanbieter-Cookies

================================================================================
6. E-MAIL-KOMMUNIKATION
================================================================================

Wir senden Ihnen folgende transaktionale E-Mails:

6.1 Verifizierungs-E-Mail

- Anlass: Nach der Registrierung
- Inhalt: Link zur Bestätigung Ihrer E-Mail-Adresse
- Gültigkeit: 24 Stunden
- Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO

6.2 Passwort-Zurücksetzung

- Anlass: Auf Anforderung bei "Passwort vergessen"
- Inhalt: 6-stelliger Einmal-Code (OTP)
- Gültigkeit: 24 Stunden
- Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO

6.3 Sicherheitsbenachrichtigung

- Anlass: Nach erfolgreicher Passwortänderung
- Inhalt: Hinweis auf Passwortänderung
- Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO

6.4 Benachrichtigung bei automatischer Inhaltslöschung

- Anlass: Wenn Inhalte aufgrund von Mehrfachmeldungen (5 oder mehr)
  automatisch entfernt werden
- Inhalt: Information über Löschung, Grund (gemeldete Kategorien),
  Hinweis auf verbleibende Verstöße bis zur Kontosperrung
- Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
  (Transparenz, Community-Schutz)

Diese E-Mails sind für die Nutzung der Plattform erforderlich und können
nicht abbestellt werden. Sie enthalten keine Werbung.

================================================================================
7. BENACHRICHTIGUNGEN
================================================================================

7.1 In-App-Benachrichtigungen

Wir informieren Sie in der Plattform über:
- Neue Kommentare zu Ihren Beiträgen
- Likes auf Ihre Beiträge
- Neue Chat-Nachrichten
- Verkauf von Marktplatz-Favoriten

7.2 Benachrichtigungseinstellungen

Sie können in Ihren Profileinstellungen festlegen, welche Benachrichtigungen
Sie erhalten möchten:
- Kommentar-Benachrichtigungen
- Like-Benachrichtigungen
- Favoriten-Benachrichtigungen
- Chat-Benachrichtigungen

Zusätzlich können Sie Benachrichtigungen für einzelne Chat-Konversationen
deaktivieren. Diese Einstellung wird pro Chat gespeichert.

Rechtsgrundlage: Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.

7.3 Push-Benachrichtigungen (optional)

Sofern Sie Push-Benachrichtigungen aktivieren, speichern wir die für den
Versand notwendigen technischen Daten:
- in der mobilen App: das Geräte-Token und die Plattform (Android/iOS)
- im Browser: die Abonnement-Daten des Browsers (Push-Adresse und
  zugehörige Schlüssel)

Rechtsgrundlage: Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO.
Sie können Push-Benachrichtigungen jederzeit in Ihren Geräte- bzw.
Browsereinstellungen deaktivieren.

================================================================================
8. WEITERGABE VON DATEN AN DRITTE
================================================================================

8.1 Auftragsverarbeiter

Wir arbeiten mit folgenden Dienstleistern zusammen:

a) E-Mail-Versand
   - Anbieter: Strato AG
   - Zweck: Versand von Transaktions-E-Mails
   - Übermittelte Daten: E-Mail-Adresse, Vorname
   - Standort: EU (Deutschland)
   - Grundlage: Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO

b) Server-Hosting
   - Anbieter: Strato AG
   - Zweck: Bereitstellung der Server-Infrastruktur
   - Standort: EU
   - Grundlage: Auftragsverarbeitungsvertrag gemäß Art. 28 DSGVO

8.2 Keine Weitergabe an Dritte

Wir verkaufen, vermieten oder teilen Ihre personenbezogenen Daten NICHT
mit Dritten zu Marketing- oder Werbezwecken.

8.3 Gesetzliche Offenlegungspflichten

Wir können Daten offenlegen, wenn wir gesetzlich dazu verpflichtet sind,
insbesondere auf Anordnung von:
- Gerichten
- Strafverfolgungsbehörden
- Datenschutzaufsichtsbehörden

Rechtsgrundlage: Rechtliche Verpflichtung gemäß Art. 6 Abs. 1 lit. c DSGVO.

================================================================================
9. DATENÜBERMITTLUNG IN DRITTLÄNDER
================================================================================

Eine Übermittlung Ihrer personenbezogenen Daten in Länder außerhalb der
Europäischen Union (EU) oder des Europäischen Wirtschaftsraums (EWR)
findet nicht statt.

================================================================================
10. SPEICHERDAUER
================================================================================

Wir speichern Ihre Daten nur so lange, wie es für die jeweiligen Zwecke
erforderlich ist:

| Datenkategorie              | Speicherdauer                                           |
|-----------------------------|---------------------------------------------------------|
| Kontodaten                  | Bis zur Kontolöschung + 30 Tage Wiederherstellungsfrist |
| Session-Tokens              | 24 Stunden                                              |
| Verifizierungs-Tokens       | 24 Stunden                                              |
| Passwort-Reset-Tokens       | 24 Stunden (oder nach Verwendung)                       |
| Blockierungsdaten           | Bis zur Aufhebung oder Kontolöschung                    |
| Beiträge und Kommentare     | Bis zur Löschung durch Nutzer                           |
| Marktplatz-Anzeigen         | Bis zur Löschung durch Nutzer                           |
| Chat-Nachrichten            | Bis zur Löschung des Gesprächs                          |
| Kursbewertungen             | Bis zur Löschung durch Nutzer                           |
| Profilbilder                | Bis zur Entfernung oder Kontolöschung                   |
| Nutzungsstatistiken         | Bis zur Kontolöschung; danach nur noch anonym/aggregiert|
| Feedback und Bewertungen    | Bis zur Kontolöschung                                   |
| Push-Abonnements            | Bis zur Abmeldung oder Kontolöschung                    |
| Server-Logs                 | 30 Tage                                                 |
| Fehlerprotokolle            | 30 Tage                                                 |
| Backup-Daten                | 90 Tage                                                 |

Bei Kontolöschung werden alle mit Ihrem Konto verknüpften Daten gelöscht,
sofern keine gesetzlichen Aufbewahrungspflichten entgegenstehen.

10.1 Wiederherstellungsfrist bei Kontolöschung

Wenn Sie die Löschung Ihres Kontos beantragen, beginnt eine 30-tägige
Wiederherstellungsfrist:

- Während dieser 30 Tage können Sie Ihr Konto durch erneuten Login
  vollständig wiederherstellen
- Ihre Daten bleiben während dieser Zeit gespeichert, sind aber nicht
  aktiv (kein Zugriff durch andere Nutzer)
- Nach Ablauf der 30 Tage werden personenbezogene Daten sowie Ihre
  Beiträge, Kommentare, Marktplatzangebote, Chat-Nachrichten und
  hochgeladenen Bilder unwiderruflich gelöscht
- Kursbewertungen werden nicht gelöscht, sondern anonymisiert (Ihr Name
  wird entfernt; die Bewertung bleibt als "Anonym" erhalten)

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Schutz vor versehentlicher Löschung, Wiederherstellungsmöglichkeit).

================================================================================
11. IHRE RECHTE ALS BETROFFENE PERSON
================================================================================

Sie haben gegenüber uns folgende Rechte bezüglich Ihrer personenbezogenen
Daten:

11.1 Auskunftsrecht (Art. 15 DSGVO)

Sie haben das Recht, eine Bestätigung darüber zu verlangen, ob wir
personenbezogene Daten von Ihnen verarbeiten. Ist dies der Fall, haben
Sie ein Recht auf Auskunft über diese Daten sowie weitere Informationen
gemäß Art. 15 DSGVO.

11.2 Recht auf Berichtigung (Art. 16 DSGVO)

Sie haben das Recht, unverzüglich die Berichtigung unrichtiger oder
Vervollständigung unvollständiger personenbezogener Daten zu verlangen.

11.3 Recht auf Löschung (Art. 17 DSGVO)

Sie haben das Recht, die Löschung Ihrer personenbezogenen Daten zu
verlangen, sofern nicht die Verarbeitung zur Erfüllung einer rechtlichen
Verpflichtung erforderlich ist.

Um Ihr Konto und alle damit verbundenen Daten zu löschen, nutzen Sie
bitte die Funktion "Konto löschen" in Ihren Profileinstellungen oder
kontaktieren Sie uns per E-Mail. Was bei der Löschung mit Ihren Inhalten
geschieht, ist in Abschnitt 10.1 beschrieben.

11.4 Recht auf Einschränkung der Verarbeitung (Art. 18 DSGVO)

Sie haben das Recht, die Einschränkung der Verarbeitung zu verlangen,
insbesondere wenn:
- Sie die Richtigkeit der Daten bestreiten
- Die Verarbeitung unrechtmäßig ist
- Wir die Daten nicht mehr benötigen

11.5 Recht auf Datenübertragbarkeit (Art. 20 DSGVO)

Sie haben das Recht, Ihre personenbezogenen Daten in einem strukturierten,
gängigen und maschinenlesbaren Format zu erhalten. Nutzen Sie hierfür
die Funktion "Daten exportieren" in Ihren Profileinstellungen.

11.6 Widerspruchsrecht (Art. 21 DSGVO)

Sie haben das Recht, aus Gründen, die sich aus Ihrer besonderen Situation
ergeben, jederzeit gegen die Verarbeitung Sie betreffender personenbezogener
Daten, die aufgrund von Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse)
erfolgt, Widerspruch einzulegen.

11.7 Recht auf Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO)

Soweit die Verarbeitung auf Ihrer Einwilligung beruht (z.B. Push-
Benachrichtigungen), haben Sie das Recht, diese jederzeit zu widerrufen.
Der Widerruf berührt nicht die Rechtmäßigkeit der bis dahin erfolgten
Verarbeitung.

11.8 Beschwerderecht bei einer Aufsichtsbehörde (Art. 77 DSGVO)

Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über
die Verarbeitung Ihrer personenbezogenen Daten zu beschweren.

================================================================================
12. AUFSICHTSBEHÖRDE
================================================================================

Zuständige Aufsichtsbehörde:

Unabhängiges Landeszentrum für Datenschutz Schleswig-Holstein (ULD)
Holstenstraße 98
24103 Kiel
E-Mail: mail@datenschutzzentrum.de
Telefon: 0431 988-1200
Website: datenschutzzentrum.de

================================================================================
13. DATENSICHERHEIT
================================================================================

Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um
Ihre Daten gegen zufällige oder vorsätzliche Manipulationen, Verlust,
Zerstörung oder den Zugriff unberechtigter Personen zu schützen:

13.1 Technische Maßnahmen

- Verschlüsselte Datenübertragung nach dem Stand der Technik (TLS/HTTPS)
- Sichere Speicherung von Passwörtern mittels anerkannter Hash-Verfahren
  (Passwörter werden zu keinem Zeitpunkt im Klartext gespeichert)
- Geschützte, zeitlich begrenzte Anmeldesitzungen
- Schutz vor gängigen Angriffen (z.B. auf Datenbank und Dateiuploads)
- Zugangsbeschränkung auf die Registrierung mit Hochschul-E-Mail-Adressen

13.2 Organisatorische Maßnahmen

- Beschränkter Zugang zu personenbezogenen Daten
- Regelmäßige Sicherheitsüberprüfungen
- Dokumentierte Datenschutzprozesse

================================================================================
14. INHALTSMODERATION
================================================================================

Zum Schutz unserer Nutzer-Community führen wir eine Inhaltsmoderation
durch:

14.1 Meldungen durch Nutzer

Nutzer können unangemessene Inhalte melden. Wir speichern dabei:
- Gemeldeten Inhalt
- Meldekategorie (z.B. Hassrede, Spam, Bedrohung)
- Optionale Begründung

14.2 Automatische Maßnahmen

- Beiträge mit mehreren Meldungen werden überprüft
- Bei schweren oder wiederholten Verstößen kann eine Kontosperrung erfolgen

14.2a Wortfilter und automatische Inhaltserkennung

Vor dem Hochladen von Inhalten führen wir eine automatische Prüfung auf
unangemessene Wörter oder Ausdrücke durch:

- Verarbeitung: Der Inhalt wird lokal geprüft, bevor er auf dem Server
  gespeichert wird
- Bei Erkennung unangemessener Wörter: Der Upload wird blockiert und
  der Nutzer wird informiert, welche Wörter den Upload verhindern
- Es werden keine detaillierten Logs über blockierte Inhalte gespeichert
- Die Liste der gefilterten Wörter wird nicht veröffentlicht

14.2b Automatische Löschung bei Mehrfachmeldungen

Inhalte, die von 5 oder mehr verschiedenen Nutzern gemeldet werden,
werden automatisch entfernt:

- Der Autor wird per E-Mail über die Löschung informiert
- Die Meldekategorien werden in der E-Mail genannt
- Der Autor kann Widerspruch beim Kundenservice einlegen
- Wiederherstellung ist durch Administratoren möglich
- Wird ein Inhalt wiederhergestellt, erfolgt eine erneute automatische
  Löschung erst nach 5 weiteren Meldungen

Rechtsgrundlage: Berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO
(Schutz der Community, Plattformsicherheit).

================================================================================
15. ÄNDERUNGEN DIESER DATENSCHUTZERKLÄRUNG
================================================================================

Wir behalten uns vor, diese Datenschutzerklärung anzupassen, um sie an
geänderte Rechtslagen oder bei Änderungen des Dienstes oder der
Datenverarbeitung anzupassen.

Die aktuelle Version finden Sie stets auf unserer Plattform. Bei
wesentlichen Änderungen werden wir Sie per E-Mail oder In-App-
Benachrichtigung informieren.

Das Datum der letzten Aktualisierung finden Sie am Anfang dieser
Datenschutzerklärung.

================================================================================
16. KONTAKT
================================================================================

Bei Fragen zur Verarbeitung Ihrer personenbezogenen Daten, zur Ausübung
Ihrer Rechte oder zu dieser Datenschutzerklärung wenden Sie sich bitte an:

datenschutz@universe-app.online

================================================================================

Stand: 1. Juli 2026

================================================================================`
  },
  guidelines: {
    title: 'Community-Richtlinie',
    titleEn: 'Community Guidelines',
    content: `UNIVERSE COMMUNITY GUIDELINE
Gemeinschaftsrichtlinien

Stand: 11. Juli 2026

================================================================================
HINWEIS ZUR SPRACHE
================================================================================

Diese Community Guideline ist ausschließlich in deutscher Sprache verfügbar.
Als Plattform für Studierende an deutschen Hochschulen haben wir uns
entschieden, unsere Gemeinschaftsrichtlinien nur auf Deutsch anzubieten.

This Community Guideline is only available in German. As a platform for
students at German universities, we have decided to provide our community
guidelines in German only.

================================================================================
WILLKOMMEN BEI UNIVERSE
================================================================================

Hallo und herzlich willkommen in unserer Community!

UNIVERSE ist mehr als nur eine App - wir sind eine Gemeinschaft von
Studierenden an deutschen Hochschulen. Hier kannst du dich mit anderen
Studis vernetzen, im Marktplatz Sachen kaufen und verkaufen, Kurse
bewerten und dich über das Unileben austauschen.

Damit UNIVERSE ein positiver, sicherer und unterstützender Ort für alle
bleibt, haben wir diese Gemeinschaftsrichtlinien erstellt. Sie gelten für
alle Inhalte und Interaktionen auf unserer Plattform - egal ob Beiträge,
Kommentare, Marktplatz-Anzeigen, Kursbewertungen oder Chatnachrichten.

Bitte lies dir diese Richtlinien sorgfältig durch. Mit der Nutzung von
UNIVERSE erklärst du dich damit einverstanden, diese Regeln einzuhalten.

================================================================================
1. UNSERE GRUNDWERTE
================================================================================

1.1 Respekt und Wertschätzung

Behandle andere so, wie du selbst behandelt werden möchtest. Hinter jedem
Account steckt ein echter Mensch - ein Mitstudi, der genauso wie du sein
Studium meistern will.

1.2 Ehrlichkeit und Authentizität

Sei authentisch. Steh zu deinen Aussagen und verbreite keine Falsch-
informationen. Vertrauen ist die Basis unserer Community.

1.3 Inklusivität und Vielfalt

UNIVERSE ist für alle Studierenden da, unabhängig von Herkunft,
Geschlecht, sexueller Orientierung, Religion, Behinderung oder
politischer Überzeugung. Diskriminierung hat bei uns keinen Platz.

1.4 Akademische Integrität

Als Studierende teilen wir gemeinsame Werte. Wir unterstützen uns
gegenseitig beim Lernen, aber wir fördern kein Schummeln oder
Plagiate.

1.5 Sicherheit

Deine Sicherheit liegt uns am Herzen. Wir arbeiten aktiv daran, UNIVERSE
zu einem sicheren Ort zu machen, aber auch du trägst dazu bei.

================================================================================
2. RESPEKTVOLLER UMGANG MITEINANDER
================================================================================

2.1 Keine Belästigung und kein Mobbing

Was wir nicht tolerieren:
- Wiederholte negative oder einschüchternde Nachrichten an eine Person
- Öffentliches Bloßstellen oder Demütigungen
- Koordinierte Angriffe auf einzelne Personen
- Drohungen jeglicher Art
- Stalking oder obsessives Verfolgen

Tipp: Wenn jemand nicht auf deine Nachrichten reagiert oder dich blockiert
hat, respektiere das. Mehrfache Kontaktversuche nach einer Ablehnung
gelten als Belästigung.

2.2 Keine Hassrede und Diskriminierung

Absolut verboten sind:
- Beleidigungen oder Herabsetzungen aufgrund von Rasse, Ethnie, Nationalität,
  Religion, Geschlecht, sexueller Orientierung, Behinderung oder Alter
- Volksverhetzung und Aufstachelung zum Hass
- Holocaust-Leugnung oder Relativierung
- Verwendung verfassungswidriger Symbole (Hakenkreuze, SS-Runen, etc.)
- Verherrlichung von Nationalsozialismus oder anderen extremistischen Ideologien

Hinweis: Diese Inhalte sind in Deutschland strafbar (StGB §§ 86, 86a, 130).
Wir sind gesetzlich verpflichtet, solche Inhalte zu entfernen und ggf. an
Behörden zu melden.

2.3 Respektiere die Privatsphäre anderer

Veröffentliche keine:
- Privaten Daten anderer Personen (Adresse, Telefonnummer, etc.) ohne Erlaubnis
- Fotos oder Videos, auf denen andere erkennbar sind, ohne deren Zustimmung
- Private Nachrichten oder Gespräche ohne Einverständnis
- Intime oder kompromittierende Aufnahmen (auch wenn du sie freiwillig
  erhalten hast)

2.4 Keine Gewalt und Drohungen

Wir verbieten strikt:
- Androhung von körperlicher Gewalt
- Aufforderung zu Gewalt gegen Personen oder Gruppen
- Verherrlichung von Gewalt, Terrorismus oder gewalttätigen Ideologien
- Detaillierte Darstellung von realer Gewalt oder Folter
- Doxxing (Veröffentlichung persönlicher Daten mit schädlicher Absicht)

Wichtig: Drohungen werden ernst genommen. Bei konkreten Gewaltandrohungen
informieren wir die Polizei.

2.5 Keine sexuelle Belästigung

Verboten sind:
- Unaufgeforderte sexuelle Nachrichten oder Bilder
- Sexuelle Kommentare oder Anfragen, die nicht erwünscht sind
- Verbreitung intimer Aufnahmen ohne Zustimmung ("Revenge Porn")
- Jegliche Form von sexueller Nötigung oder Erpressung

================================================================================
3. VERBOTENE INHALTE
================================================================================

3.1 Illegale Inhalte

Folgende Inhalte sind auf UNIVERSE strengstens verboten:

a) Kindesmissbrauchsdarstellungen (CSAM)
   - Jegliche sexuelle Darstellung von Minderjährigen
   - Wir sind gesetzlich verpflichtet, solche Inhalte sofort dem
     Bundeskriminalamt (BKA) zu melden
   - Null-Toleranz-Politik: Sofortige Kontosperrung und Anzeige

b) Drogen und kontrollierte Substanzen
   - Verkauf, Kauf oder Tausch von illegalen Drogen
   - Anleitung zur Drogenherstellung
   - Werbung für Drogenkonsum
   - Verkauf von verschreibungspflichtigen Medikamenten ohne Rezept

c) Waffen und gefährliche Gegenstände
   - Verkauf von Schusswaffen, Messern (über 12 cm Klingenlänge),
     Schlagringen, Elektroschockern
   - Anleitungen zum Bau von Waffen oder Sprengstoffen
   - Feuerwerk und pyrotechnische Artikel

d) Betrug und illegale Aktivitäten
   - Betrugsversuche jeglicher Art
   - Identitätsdiebstahl
   - Phishing oder Verbreitung von Malware
   - Geldwäsche oder Hehlerei
   - Gefälschte Dokumente (Studentenausweise, Zeugnisse, etc.)

3.2 Sexuelle Inhalte

Nicht erlaubt sind:
- Pornografische Inhalte
- Explizit sexuelle Darstellungen
- Inhalte, die sexuelle Dienstleistungen anbieten
- Nacktheit in sexualisiertem Kontext

Ausnahme: Künstlerische oder edukative Darstellungen können im Einzelfall
zulässig sein, müssen aber mit einer Inhaltswarnung versehen werden.

3.3 Gewaltverherrlichende Inhalte

Verboten:
- Detaillierte Darstellungen von Gewalt gegen Menschen oder Tiere
- Verherrlichung von Terrorismus oder terroristischen Organisationen
- Gewaltaufrufe
- Inhalte, die Selbstverletzung oder Suizid fördern

Hinweis: Wenn du dich in einer Krise befindest, wende dich bitte an die
Telefonseelsorge: 0800 111 0 111 oder 0800 111 0 222 (kostenlos, 24/7)

3.4 Spam und Manipulation

Wir tolerieren nicht:
- Massenhaftes Posten identischer oder ähnlicher Inhalte
- Fake-Accounts oder Bots
- Künstliche Aufblähung von Likes oder Kommentaren
- Irreführende Links oder Clickbait
- Kommerzielle Werbung ohne Kennzeichnung

3.5 Falschinformationen

Verboten sind:
- Offensichtlich falsche Gesundheitsinformationen, die Schaden anrichten können
- Fälschung von Nachrichten oder offiziellen Mitteilungen
- Ausgeben als Institution, Behörde oder bekannte Persönlichkeit
- Erfundene Notfälle oder Katastrophenmeldungen

================================================================================
4. MARKTPLATZ-REGELN
================================================================================

4.1 Was du verkaufen darfst

Grundsätzlich erlaubt:
- Gebrauchte Bücher und Lernmaterialien
- Kleidung und Accessoires
- Elektronik (funktionstüchtig)
- Möbel und Einrichtung
- Sport- und Freizeitartikel
- Selbstgemachtes und Handarbeit
- Tickets für Veranstaltungen (zum Originalpreis)

4.2 Verbotene Artikel

Auf UNIVERSE darfst du NICHT verkaufen:
- Waffen aller Art (inkl. Messer über 12cm, Softair-Waffen)
- Drogen und Medikamente
- Alkohol und Tabak
- Nazi-Memorabilien oder verfassungswidrige Symbole
- Gefälschte Markenprodukte
- Gestohlene Waren
- Gefährliche Chemikalien oder Feuerwerkskörper
- Tiere (lebend oder tot)
- Pornografisches Material
- Akademische Arbeiten (Hausarbeiten, Abschlussarbeiten zum Verkauf)
- Gefälschte Dokumente oder Ausweise

4.3 Anzeigen-Anforderungen

Jede Anzeige muss enthalten:
- Eine ehrliche Beschreibung des Artikels (mind. 20 Zeichen)
- Mindestens ein echtes Foto des tatsächlichen Artikels
  (keine Stockfotos oder Bilder aus dem Internet!)
- Einen Preis oder die Angabe "Verhandlungsbasis"
- Den Zustand des Artikels (Neu / Wie neu / Gut / Akzeptabel)
- Information zu Abholung oder Versand

4.4 Faire Preise und Ehrlichkeit

- Beschreibe den Zustand ehrlich - erwähne Mängel und Gebrauchsspuren
- Setze realistische Preise
- Antworte zügig auf Anfragen
- Halte Zusagen ein

4.5 Sicher Handeln

Tipps für sichere Transaktionen:
- Triff dich an öffentlichen Orten auf dem Campus
- Nimm zur Sicherheit jemanden mit
- Überprüfe den Artikel vor der Bezahlung
- Zahle möglichst bar bei Abholung
- Gib NIEMALS deine Bankdaten oder Passwörter im Chat weiter
- Bei Versand: Nutze sichere Zahlungsmethoden

Achtung: Bei verdächtigen Angeboten (zu guter Preis, Vorkasse ins Ausland,
etc.) brich den Kontakt ab und melde die Anzeige!

4.6 Keine Werbung für externe Dienste

Der Marktplatz ist für private Verkäufe zwischen Studierenden gedacht.
Nicht erlaubt:
- Werbung für externe Shops oder Webseiten
- Multi-Level-Marketing oder Pyramidensysteme
- Dauerhafte gewerbliche Verkäufe (Gewerbe anmelden!)

================================================================================
5. KURSBEWERTUNGEN UND AKADEMISCHE INHALTE
================================================================================

5.1 Kursbewertungen - So geht's richtig

Kursbewertungen helfen anderen Studierenden, gute Kurse zu finden.
Damit sie nützlich bleiben, beachte folgende Regeln:

Erlaubt und erwünscht:
- Deine ehrliche Meinung zur Qualität des Kurses
- Einschätzung zu Schwierigkeit, Arbeitsaufwand und Organisation
- Konstruktive Kritik
- Tipps für andere Studierende
- Lob für gute Lehre

Nicht erlaubt:
- Persönliche Angriffe auf Lehrende (Aussehen, Privatleben, etc.)
- Falsche Tatsachenbehauptungen (z.B. "Prof. X hat plagiiert")
- Koordinierte Absprachen für negative Bewertungen
- Beleidigungen oder Beschimpfungen
- Veröffentlichung privater Informationen über Lehrende

Wichtig: Eine kritische Bewertung ist erlaubt - eine Beleidigung nicht!
Es ist ein Unterschied, ob du schreibst "Die Vorlesung war unstrukturiert
und schwer zu folgen" oder "Prof. X ist inkompetent und hässlich".

5.2 Lehrende können antworten

Wir geben Lehrenden die Möglichkeit, auf Bewertungen zu reagieren.
So entsteht ein fairer Dialog.

5.3 Teilen von Lernmaterialien

Erlaubt:
- Deine eigenen Notizen und Zusammenfassungen
- Von dir erstellte Lernhilfen und Karteikarten
- Links zu frei verfügbaren Ressourcen
- Diskussionen über Kursinhalte

Nicht erlaubt (Urheberrechtsverletzung!):
- Vollständige Lehrbücher oder Buchkapitel
- Vorlesungsfolien ohne Genehmigung des Dozenten
- Unveröffentlichte Forschung
- Prüfungen des aktuellen Semesters
- Lösungen zu bewerteten Aufgaben

Hinweis: Das Hochladen urheberrechtlich geschützter Materialien ist
nach deutschem Recht strafbar (UrhG). Wir müssen solche Inhalte entfernen.

5.4 Akademische Integrität

UNIVERSE ist ein Ort zum Lernen und zum Austausch - aber kein Ort zum
Schummeln. Wir unterstützen keine akademische Unredlichkeit.

Verboten:
- Angebote, Hausarbeiten oder Abschlussarbeiten für andere zu schreiben
- Verkauf von Prüfungslösungen
- Organisierte Betrugsversuche bei Klausuren
- Identitätsbetrug bei Prüfungen

================================================================================
6. NACHRICHTEN UND CHAT
================================================================================

6.1 Respektvolle Kommunikation

Der Chat ist für die Kommunikation rund um den Marktplatz gedacht.
Bitte beachte:

- Sei höflich und respektvoll
- Antworte zeitnah auf Anfragen
- Versende keine unerwünschten Werbebotschaften
- Respektiere es, wenn jemand nicht antworten möchte

6.2 Was im Chat verboten ist

- Belästigung oder Stalking
- Unaufgeforderte sexuelle Nachrichten oder Bilder
- Drohungen oder Einschüchterung
- Spam oder Kettennachrichten
- Betrugsversuche
- Links zu schädlichen Webseiten

6.3 Blockieren und Melden

Wenn dich jemand belästigt:
1. Blockiere die Person - sie kann dir dann keine Nachrichten mehr senden
2. Melde den Chat, wenn ein Verstoß vorliegt
3. Speichere ggf. Beweise (Screenshots)

================================================================================
7. WAS PASSIERT BEI VERSTÖSSEN?
================================================================================

7.1 Unser Stufensystem

Wir glauben an zweite Chancen - aber auch an Konsequenzen. Je nach
Schwere des Verstoßes reagieren wir unterschiedlich:

Stufe 1 - Verwarnung
- Bei leichten oder erstmaligen Verstößen
- Der Inhalt wird entfernt
- Du erhältst eine Benachrichtigung mit Erklärung
- Der Verstoß wird notiert (verfällt nach 6 Monaten)

Stufe 2 - Einschränkung
- Bei wiederholten Verstößen innerhalb von 6 Monaten
- Der Inhalt wird entfernt
- Temporäre Einschränkung der Posting-Funktion (7 Tage)
- Der Verstoß wird notiert (verfällt nach 12 Monaten)

Stufe 3 - Suspendierung
- Bei mehrfachen Verstößen innerhalb von 12 Monaten
- Dein Account wird für 30 Tage gesperrt
- Der Verstoß wird notiert (verfällt nach 24 Monaten)

Stufe 4 - Permanente Sperre
- Bei wiederholten schweren Verstößen
- Oder bei einmaligen besonders schweren Verstößen
- Dein Account wird dauerhaft gesperrt
- Du kannst keinen neuen Account erstellen

7.2 Sofortige Sperrung ohne Vorwarnung

Bei folgenden Verstößen erfolgt eine sofortige permanente Sperre:
- Kindesmissbrauchsdarstellungen (plus Anzeige)
- Konkrete Gewaltdrohungen (plus ggf. Polizeimeldung)
- Verbreitung intimer Aufnahmen ohne Zustimmung
- Betrug im Marktplatz
- Hassrede und Volksverhetzung
- Verwendung verfassungswidriger Symbole

7.3 Entfernte Inhalte

Wenn wir Inhalte entfernen:
- Erhältst du eine Benachrichtigung mit der Begründung
- Wird dir mitgeteilt, welche Regel verletzt wurde
- Erfährst du, welche Konsequenzen das hat
- Wirst du über dein Recht auf Widerspruch informiert

Automatische Entfernung bei Mehrfachmeldungen:
- Wenn 5 oder mehr verschiedene Nutzer deinen Inhalt melden, wird er
  automatisch entfernt (dies dient dem Schutz der Community)
- Du erhältst per E-Mail eine Benachrichtigung mit:
  - Den gemeldeten Kategorien (z.B. Belästigung, Spam, etc.)
  - Der Anzahl der verbleibenden Verstöße bis zur Kontosperrung
  - Hinweisen zum Widerspruchsverfahren
- Du kannst Widerspruch einlegen, und ein Moderator prüft den Fall

Wir speichern entfernte Inhalte für 10 Wochen, um mögliche Widersprüche
prüfen und unseren Dokumentations- und Rechenschaftspflichten nachkommen
zu können (berechtigtes Interesse gemäß Art. 6 Abs. 1 lit. f DSGVO; im
Einklang mit den Vorgaben des Digital Services Act (DSA) zu Beschwerde-
und Rechtsbehelfsverfahren).

================================================================================
8. MELDEN VON VERSTÖSSEN
================================================================================

8.1 So meldest du Inhalte

Wenn du einen Verstoß gegen unsere Richtlinien bemerkst:

1. Tippe auf das Melden-Symbol (Flagge oder drei Punkte) beim Inhalt
2. Wähle die passende Kategorie:
   - Illegaler Inhalt
   - Belästigung/Mobbing
   - Hassrede/Diskriminierung
   - Spam
   - Marktplatz-Verstoß
   - Akademische Integrität
   - Sonstiges
3. Ergänze optional weitere Details
4. Sende die Meldung ab

8.2 Was passiert nach deiner Meldung?

1. Du erhältst eine Bestätigung, dass wir deine Meldung erhalten haben
2. Unser Team prüft den gemeldeten Inhalt
3. Wir treffen eine Entscheidung basierend auf unseren Richtlinien
4. Du wirst informiert, ob Maßnahmen ergriffen wurden

Bearbeitungszeiten:
- Offensichtlich illegale Inhalte: innerhalb von 24 Stunden
- Andere Verstöße: innerhalb von 7 Tagen

8.3 Deine Anonymität ist geschützt

Die gemeldete Person erfährt NICHT, wer die Meldung abgegeben hat.
Wir schützen die Identität von Meldern.

8.4 Falschmeldungen

Bitte melde nur echte Verstöße. Wiederholte Falschmeldungen
(um jemandem zu schaden) können selbst als Verstoß gewertet werden.

================================================================================
9. DEIN RECHT AUF WIDERSPRUCH
================================================================================

9.1 Wenn du nicht einverstanden bist

Wenn wir einen deiner Inhalte entfernt oder deinen Account eingeschränkt
haben und du damit nicht einverstanden bist, hast du das Recht auf
Widerspruch.

Stufe 1 - Interner Widerspruch
- Du hast 14 Tage Zeit nach unserer Entscheidung
- Nutze das Widerspruchsformular in der App
- Erkläre, warum du die Entscheidung für falsch hältst
- Ein anderer Moderator als der ursprüngliche prüft deinen Fall
- Antwort innerhalb von 7 Werktagen

Stufe 2 - Zweitprüfung
- Wenn du mit Stufe 1 nicht einverstanden bist
- Du hast 7 Tage Zeit nach der ersten Entscheidung
- Prüfung durch unser Senior-Team
- Antwort innerhalb von 14 Werktagen
- Finale interne Entscheidung

Stufe 3 - Externe Streitbeilegung
- Du kannst dich an eine zertifizierte Streitbeilegungsstelle wenden
- Informationen dazu erhältst du in der Ablehnungsbegründung
- Für dich kostenfrei (wir tragen die Kosten)

9.2 Wenn dein Widerspruch erfolgreich ist

- Der Inhalt wird innerhalb von 24 Stunden wiederhergestellt
- Der Verstoß wird aus deiner Akte gelöscht
- Du wirst darüber informiert

================================================================================
10. DATENSCHUTZ BEI DER MODERATION
================================================================================

10.1 Wie wir Inhalte prüfen

Wenn ein Inhalt gemeldet wird oder unsere automatischen Systeme
ihn markieren:
- Wird er von geschulten Moderatoren geprüft
- Beachten wir den Kontext des Beitrags
- Treffen wir eine Entscheidung basierend auf diesen Richtlinien
- Dokumentieren wir die Entscheidung

10.2 Automatische Erkennung

Wir nutzen technische Systeme zur Erkennung von:
- Kindesmissbrauchsdarstellungen
- Bekannten terroristischen Inhalten
- Offensichtlichem Spam
- Verdächtigen Marktplatz-Anzeigen
- Unangemessenen Wörtern und Ausdrücken

Diese Systeme unterstützen uns, aber finale Entscheidungen werden
von Menschen getroffen.

10.2a Wortfilter und Inhaltsprüfung

Bevor dein Inhalt veröffentlicht wird, prüft unser System automatisch
auf unangemessene Wörter oder Ausdrücke:

- Wenn unangemessene Wörter erkannt werden, wird der Upload blockiert
- Du erhältst eine Nachricht, die dir zeigt, welche Wörter den Upload
  verhindert haben
- Du kannst deinen Inhalt dann anpassen und erneut versuchen
- Dies dient dem Schutz der Community und hilft dir, unsere Regeln
  einzuhalten

Diese präventive Prüfung ersetzt nicht die manuelle Moderation. Auch
Inhalte ohne blockierte Wörter können gegen unsere Richtlinien verstoßen.

10.3 Speicherung

- Entfernte Inhalte: 10 Wochen (zur Bearbeitung von Widersprüchen; berechtigtes Interesse, Art. 6 Abs. 1 lit. f DSGVO, im Einklang mit dem DSA)
- Meldungen: Bis zur Entscheidung + 90 Tage
- Verstoß-Historie: Je nach Stufe (6-24 Monate)
- Bei erfolgreichem Widerspruch: Sofortige Löschung der Verstoß-Notiz

10.4 Deine Auskunftsrechte

Du kannst jederzeit anfragen:
- Welche Verstöße in deiner Akte notiert sind
- Welche Meldungen du abgegeben hast
- Welche Entscheidungen zu deinen Inhalten getroffen wurden

Details findest du in unserer Datenschutzerklärung.

================================================================================
11. ZUSAMMENARBEIT MIT BEHÖRDEN
================================================================================

11.1 Wann wir Behörden informieren

In folgenden Fällen sind wir gesetzlich verpflichtet, Inhalte an
Behörden zu melden:

- Kindesmissbrauchsdarstellungen -> Bundeskriminalamt (BKA)
- Konkrete Gewaltandrohungen -> Polizei
- Terroristische Inhalte -> Bundeskriminalamt (BKA)

11.2 Anfragen von Behörden

Wir kooperieren mit Strafverfolgungsbehörden bei:
- Gerichtlich angeordneten Anfragen
- Staatsanwaltschaftlichen Ermittlungen
- Polizeilichen Notfällen (Gefahr für Leben)

Soweit rechtlich möglich, informieren wir dich, wenn Behörden
Daten zu deinem Account angefragt haben.

================================================================================
12. ÄNDERUNGEN DIESER RICHTLINIEN
================================================================================

Wir können diese Gemeinschaftsrichtlinien von Zeit zu Zeit aktualisieren,
um auf neue Entwicklungen, rechtliche Anforderungen oder Feedback aus
unserer Community zu reagieren.

Bei wesentlichen Änderungen:
- Informieren wir dich per E-Mail oder In-App-Benachrichtigung
- Geben wir dir Zeit, dich mit den Änderungen vertraut zu machen
- Tritt die neue Version 14 Tage nach Veröffentlichung in Kraft

Die aktuelle Version findest du immer in der App unter
Profil > Community Guideline.

================================================================================
13. KONTAKT
================================================================================

Hast du Fragen zu diesen Richtlinien oder möchtest du einen Verstoß
melden, der nicht über die App-Funktion möglich ist?

E-Mail: community@universe-app.online

================================================================================
14. FAZIT
================================================================================

UNIVERSE lebt von seiner Community - von dir und allen anderen Studierenden,
die hier ihre Erfahrungen teilen, sich gegenseitig helfen und vernetzen.

Mit diesen Richtlinien wollen wir sicherstellen, dass UNIVERSE ein Ort
bleibt, an dem sich alle willkommen und sicher fühlen. Danke, dass du
Teil unserer Community bist und dazu beiträgst, sie positiv zu gestalten!

Dein UNIVERSE Team

================================================================================

Stand: 11. Juli 2026

================================================================================`
  },
  impressum: {
    title: 'Impressum',
    titleEn: 'Legal Notice',
    content: `================================================================================
                           IMPRESSUM
================================================================================

Angaben gemäß § 5 TMG:

GAIA Software UG (haftungsbeschränkt)
Gerhardstraße 6
22926 Ahrensburg
Deutschland

Geschäftsführer:
Jihyun Lee, Julius von Sulecki

Kontakt:
E-Mail: kontakt@universe-app.online

Registereintrag:
Registergericht: Amtsgericht Lübeck
Registernummer: HRB-27728

Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:
Jihyun Lee, Julius von Sulecki
Gerhardstraße 6
22926 Ahrensburg
Deutschland
`
  }
};

/**
 * LegalDocumentModal Component
 * @param {string} documentType - Type of document: 'terms', 'privacy', 'guidelines', 'impressum'
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 */
const LegalDocumentModal = ({ documentType, isOpen, onClose }) => {
  const { isGerman } = useLanguage();
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  const document = LEGAL_CONTENT[documentType];

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  // Focus management and body scroll lock
  useEffect(() => {
    if (isOpen) {
      // Store the currently focused element
      previousFocusRef.current = window.document.activeElement;

      // Focus the close button when modal opens
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 0);

      // Lock body scroll
      window.document.body.style.overflow = 'hidden';

      return () => {
        // Restore body scroll
        window.document.body.style.overflow = '';

        // Restore focus to previous element
        previousFocusRef.current?.focus();
      };
    }
  }, [isOpen]);

  // Focus trap within modal
  const handleTabKey = (event) => {
    if (event.key !== 'Tab' || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && window.document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && window.document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  if (!isOpen || !document) return null;

  const title = isGerman ? document.title : document.titleEn;
  const backLabel = isGerman ? 'Zurück' : 'Back';
  const closeLabel = isGerman ? 'Schließen' : 'Close';

  return (
    <div
      className="legal-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={handleTabKey}
    >
      <div className="legal-modal" ref={modalRef}>
        {/* Header */}
        <header className="legal-modal-header">
          <button
            ref={closeButtonRef}
            className="legal-modal-back-button"
            onClick={onClose}
            aria-label={closeLabel}
            type="button"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="legal-modal-back-text">{backLabel}</span>
          </button>
          <h1 id="legal-modal-title" className="legal-modal-title">{title}</h1>
        </header>

        {/* Content */}
        <main className="legal-modal-content">
          <div className="legal-modal-text-container">
            <pre className="legal-modal-text">{document.content}</pre>
          </div>
        </main>
      </div>
    </div>
  );
};

export default LegalDocumentModal;
